// The app's manual — per-screen help text, keyed by route. This ships with the
// app (it documents the app itself, and versions with it); it is deliberately
// NOT Pod config, unlike the institution's identity, which varies per
// deployment. Each entry is app-authored HTML rendered by HelpDrawer via
// {@html}; screens whose "for reference" block needs live Pod data pass that in
// as a `children` snippet at the call site instead.
//
// Voice: `whatFor` addresses the signed-in person in the imperative — what to do
// here; `howItWorks` explains the mechanics the screen touches; `reference`
// carries the longer background (the migrated "for reference" prose and the
// full text of the teaching notes that on-screen were reduced to one-line hints).

export const HELP = {
  login: {
    title: 'Signing in',
    whatFor: `<p>Sign in with the email and password your SolidShare operator
      gave you. You can change the password afterwards under your name, top
      right.</p>`,
    howItWorks: `<p>You are <em>not</em> signing in as a Solid identity. The
      institution has exactly one WebID, held by the backend as client
      credentials — that is the identity partners grant access to. Your login
      is internal to the institution. It carries roles — manager, data steward,
      analyst — which the backend enforces (it rejects, for example, a publish
      by a login without the manager role).</p>`,
    reference: `<p>This Pod talks to exactly one app, and this app serves exactly
      one institution: the relationship is 1:1. Solid's interactive OIDC login
      exists to let <em>many</em> apps act for <em>many</em> people against one
      Pod — real machinery for a real problem, but not this one. So the app holds
      the institution's credential in its backend (the same mechanism the
      connector's system WebID already uses) and tells staff apart by their own
      logins.</p>
      <p>What this costs is named in <code class="mono">tech-spec.md</code>: the
      backend holds a credential with full Pod access and every partner grant, so
      it must be run by the party the institution trusts — the network, or the
      institution itself, never a vendor. Interactive OIDC per person, with
      bring-your-own identity provider, stays the more decentralised end state.</p>`,
  },

  overview: {
    title: 'Overview',
    whatFor: `<p>Where things stand: each dataset with how much it holds and who
      it is shared with (open one for its terms and sharing), what partners share back, and the comparisons. How the
      data is made (sources, records, cubes) is under <strong>Advanced</strong>.</p>`,
    howItWorks: `<p>The amounts come from statistics kept in your Pod, refreshed
      after every ingest and cube change; when they are missing, a data steward
      rebuilds them under Advanced. Partner status is checked live. Every Pod
      read happens server-side under the institutional credential — the browser
      never holds a token.</p>`,
  },

  dataset: {
    title: 'Dataset',
    whatFor: `<p><strong>About</strong> says how much there is and where it comes
      from. <strong>Terms</strong> lists what it records, in the vocabulary's
      words, grouped by the schema each comes from. <strong>Sharing</strong> says which partner receives which counts
      and under which rules. <strong>Under the hood</strong> leads to the
      technical screens behind it.</p>`,
    howItWorks: `<p>Only counts are ever shared: each one counts the values of
      one or two terms per period, and small counts are withheld before
      anything leaves the Pod. A term marked "never" is dropped when the
      export is read in and is not stored.</p>`,
  },

  advanced: {
    title: 'Advanced',
    whatFor: `<p>The machinery behind the datasets, one card per part:
      <strong>Schemas</strong> (the terms), <strong>Sources</strong>
      (where data comes from), <strong>Records</strong> (what is stored) and
      <strong>Cubes</strong> (what is counted and shared). Each card's figures
      are per dataset.</p>`,
    howItWorks: `<p>Nothing here is stored separately. A dataset lists its record
      classes in <code class="mono">datasets.json</code>; which sources fill it
      follows from their mappings, and which cubes count it from the terms they
      count.</p>`,
  },

  schemas: {
    title: 'Schemas',
    whatFor: `<p>The terms the data is described with, one schema at a time: the
      ones this institution <strong>publishes</strong>, a <strong>draft</strong>
      made here from files (one term per column, named after it), schemas
      <strong>reused</strong> from partners as they are, and the copy of
      SolidShare <strong>core</strong> it keeps.</p>
      <p>A schema is written offline as a Turtle file. Download the draft, give
      its terms names and wording of your own, and upload it as a new version;
      upload a schema of your own; or take one a partner publishes, and its
      newer versions later.</p>`,
    howItWorks: `<p>Before a schema is replaced, the new file is checked against
      everything that uses it. A term a source fills, a cube counts or a
      comparison reads may get new wording or sharing suggestions, but may not
      disappear, move to another record class, change its kind of value or lose
      values from its list. The version must be newer; the one it replaces is
      kept, so you can go back to it.</p>
      <p>Everything here is in the Pod's <code class="mono">schema/</code> folder,
      which anyone may read, and published at <code class="mono">/schema/</code>
      on this instance, where each term's IRI leads.</p>`,
  },


  'source/ingest': {
    title: 'Ingest',
    whatFor: `<p>Read a source's export into your Pod: the rows of the stored
      export not read yet, or a new export, uploaded and read in one go. A new
      export keeps the mapping; one that lacks a column the mapping uses is
      refused until the mapping is fixed.</p>`,
    howItWorks: `<p>An export with a timestamp column is read from where the
      last ingest stopped (its watermark), so only newer rows are read. One
      without is read whole every time; its records are overwritten, not
      duplicated. Ingest runs as a background job, writes private records and
      counts your <strong>private</strong> cubes again. It does
      <strong>not</strong> touch the shared cubes your partners read: that is a
      manager's act (Partners → "Send an updated copy").</p>`,
  },

  sources: {
    title: 'Sources',
    whatFor: `<p>Every source of your data: an export from another system, such as
      a survey tool or a ticketing system, uploaded here as a CSV. Each source
      fills one or more datasets.</p>
      <p><strong>Ingest</strong> reads its waiting rows into your Pod, or
      uploads its next export under the same mapping. <strong>Mapping</strong>
      says what each of its columns is and what it publishes.</p>`,
    howItWorks: `<p>A source is a file kept by the app, a
      <code class="mono">connector.json</code> saying how to read it, and a
      <code class="mono">mapping.json</code> saying what its columns are. Ingest
      reads the file with the mapping into private records in your Pod, and
      counts the cubes again.</p>`,
  },

  'source/mapping': {
    title: 'The mapping of a source',
    whatFor: `<p>Say what each column in this source's file is, and what it
      publishes. Cubes are made on the dataset page; this is only the
      mapping.</p>
      <p>The table reads left to right as a pipeline: what the column holds in
      the file, then the three things you decide about it, then what it
      publishes. Most columns are already answered, so read it as
      <em>check these</em> rather than <em>fill this in</em>. Click any cell to
      change that one thing.</p>
      <p><strong>The shared term is the decision that matters.</strong> It is the
      agreed name for what a column means, the name your partners use for the
      same thing. Choosing it also settles the rest: which record class the
      column belongs to, and how carefully it has to be handled. Change those
      afterwards only where you disagree, and the mapping remembers that you
      did.</p>
      <p><strong>Try another export</strong> to check a new file against the
      mapping. It is read in this browser tab and not uploaded. The warnings at
      the top are the three ways a file and a mapping can disagree: columns in
      the file the mapping does not account for, columns the mapping expects
      that the file does not have, and columns still missing an answer.</p>`,
    howItWorks: `<p><strong>Three questions, and the third answers itself.</strong>
      Does this column identify a person — on its own, only in combination with
      others, or not at all? And is what it holds personal, confidential, or not
      sensitive? Those two answers decide the treatment: a column that identifies
      someone on its own is <em>not published at all</em>; one that identifies
      them in combination is <em>published only once coarsened</em>; one that
      identifies nobody but still holds something personal or confidential is
      <em>published on its own, never combined</em>; and only a column that is
      neither is <em>published freely</em>.</p>
      <p><strong>Transforms run between the two.</strong> A transform turns the
      value in the file into the value that gets published: matching a value
      onto a shared code, grouping dates into quarters, banding numbers. The
      <em>Published</em> column is the result of running the chain on the row
      you are inspecting.</p>
      <p><strong>Saving checks what uses the mapping.</strong> A cube that counts
      a column keeps the save from dropping that column, giving it another
      term, or no longer grouping its values; the message names the cube.</p>
      <p><strong>Saving changes no records yet.</strong> The records in your Pod
      were read with the mapping as it was. The next ingest reads the file again
      with the new one, and recounts the cubes.</p>`,
    reference: `<p>Save writes <code class="mono">mapping.json</code> for this
      source, one entry per column: its header, the shared term it publishes
      as, the transform chain, the two labels and the treatment they derive.
      Columns not published are listed by name in
      <code class="mono">drop</code>.</p>
      <p>"Added by the connector" columns are not from your file. The ingest
      writes them itself: the publishing institution and the period.</p>`,
  },

  records: {
    title: 'Records in Pod',
    whatFor: `<p>Browse the private records held on your Pod and open any one
      as RDF. Filter the loaded page by period. These records never leave the
      Pod — only controlled aggregates counted from them can do so.</p>`,
    howItWorks: `<p>Records are stored in batch Turtle files, in a private
      container named after their dataset (<code class="mono">belonging/</code>
      for the dataset <code class="mono">belonging</code>) and readable only by
      this institution. Each source's batches are named after it, so several
      sources can fill one dataset.</p>`,
    reference: `<p>Each record is keyed by the configured source ID strategy and
      carries <code class="mono">prov:wasDerivedFrom</code> /
      <code class="mono">prov:wasGeneratedBy</code> provenance back to the raw
      source row.</p>`,
  },

  partners: {
    title: 'Partners',
    whatFor: `<p>Manage who this institution shares with. Each partner shows the
      datasets you grant them, when their counts were last sent, and what they
      explicitly communicated as granted to you. Use "Send an updated copy" to
      republish a partner with the current counts — that is what surfaces a
      newly ingested period to them. "Change what you share" adds a dataset or
      withdraws one; withdrawing deletes the copy they have, though not what
      they already read.</p>`,
    howItWorks: `<p>The list is read from the Pod registry
      (<code class="mono">partners.json</code>) plus live directional probes: the
      outbound container lives on your Pod (present = you publish to them), the
      inbound URLs are exact containers they communicated to you. Each is probed
      directly as your institutional WebID; no reciprocal path is guessed.
      "Nothing communicated" is normal for an outbound-only partner.
      Republishing is a manager's act — the ingest job never touches shared
      containers, so a partner only sees a new period once you republish.</p>`,
  },

  partner: {
    title: 'Add sharing partner — details',
    whatFor: `<p>Identify the partner and configure either sharing direction:
      their WebID receives access to cubes you grant, while exact container URLs
      they communicated are recorded as granted by them.</p>`,
    howItWorks: `<p><strong>The WebID is the partner's identity.</strong> Access
      is granted directly to it via a WAC rule on your Pod — there is no account
      on your side. For the reverse direction, give them your institutional
      WebID and record the exact URLs they return. Knowing a URL is discovery;
      WAC still decides whether it is readable.</p>`,
  },

  'partner/access': {
    title: 'Add sharing partner — what you share',
    whatFor: `<p>Select the datasets to grant to the partner. You
      share the cubes of whole datasets, not individual records, under the
      suppression threshold and sharing policy shown with each. Leave all
      datasets unselected for an inbound-only partnership.</p>`,
    howItWorks: `<p><strong>Marginals only — never the cross.</strong> Only the
      suppressed single-axis marginal cubes are written to the partner container.
      The private records and the full cross-tabulation stay in your Pod; nothing
      that could re-identify an artist leaves it.</p>`,
  },

  'partner/confirm': {
    title: 'Add sharing partner — review directions',
    whatFor: `<p>Review granted-to and granted-by directions separately.
      Confirming records the partnership, probes communicated inbound URLs, and
      publishes outbound cubes only when datasets were selected.</p>`,
    howItWorks: `<p>Inbound URLs are never written to: the backend probes them as
      your institutional WebID and reports readable, grant pending, or
      unavailable. For outbound datasets it writes the slices, the
      <code class="mono">manifest.ttl</code> and the WAC
      <code class="mono">.acl</code> as the institutional WebID — the same
      operations as <code class="mono">demo_scripts/setup_05_partner.js</code> — and rejects
      the call outright unless you hold the manager role. A container that
      already exists answers 409 on re-publish; that is "exists", not a
      failure.</p>`,
  },

  catalog: {
    title: 'Cubes',
    whatFor: `<p>Start at the top: <strong>Your cubes</strong> is everything this
      Pod computed, whether or not anyone else can see it. Open one to read the
      actual numbers per quarter. Below that, the cubes a partner shares with
      you. <strong>Add new</strong> builds a cube a comparison needs and no
      cube counts yet.</p>`,
    howItWorks: `<p><strong>Your cubes</strong> reads your private cube
      container directly and reconciles three separate facts about each one: what
      <code class="mono">aggregations.json</code> declares, whether a cube file
      actually exists, and which partner containers hold a copy. Those three
      disagreeing is the point — a cube that was never recomputed, one kept
      deliberately private, and one whose publish never happened are three
      different problems that otherwise all look like an empty panel.</p>
      <p><strong>Private by intent is not a gap.</strong> A cube whose sharing
      mode is <code class="mono">none</code> is meant to stay here. The two states
      that need a human are <em>orphan</em> (a file with no declaration behind it,
      which nothing will ever recompute) and <em>published beyond intent</em> (a
      copy in a partner container while the declaration shares it with
      nobody).</p>
      <p><strong>An absent group is not a zero.</strong> Groups under the
      threshold are dropped before the file is written, so the cube cannot tell
      you whether a missing combination was suppressed or never occurred. And a
      crossed cube summed down to one axis undercounts, silently, for the same
      reason.</p>
      <p>The shared section reads each Pod's
      <code class="mono">manifest.ttl</code> — a fast description of every shared
      cube (dataset, dimensions, entity count, measure) written when access was
      granted, enough to profile a cube without reading one observation. A cube
      a partner sends but does not describe there is flagged.</p>`,
    reference: `<p>The catalog is driven entirely by each cube's manifest. Each
      cube carries its dataset, dimensions, measure and entity count — enough to
      profile the cube without reading a single observation.</p>`,
  },

  cubeNew: {
    title: 'New cube',
    whatFor: `<p>A comparison can only break down by something a cube already
      counts, so a missing breakdown is answered here, not in a cleverer query.
      Pick the dataset whose terms hold it, preview the counts, save. What you
      build is computed into your private container and shared with nobody until
      a manager republishes it.</p>`,
    howItWorks: `<p>A cube counts the terms of one dataset, whichever of its
      sources fills them. Their mappings decide which breakdowns may sit together
      in one cube: they say how sensitive each term is, and too many sensitive
      terms crossed in one cube would make small groups that point at single
      people. The preview counts the private records already in the Pod; nothing
      is written until you save.</p>`,
  },

  queries: {
    title: 'Comparison editor',
    whatFor: `<p>Two ways to write the same comparison, one per facet. Drafts
      survive switching between them. A breakdown no cube counts yet is built
      from Cubes → Add new.</p>
      <p><strong>Builder</strong> — pick a cube and an axis on each side, name
      the sides, preview, save. The naming is the whole point: it is where you
      assert that two differently-named axes measure the same thing, and it
      travels with the saved query as the block heading. The tool cannot work
      that out and does not try. What it does write for you are the caveats that
      follow from the cubes themselves — the crossed-cube undercount, the
      differing vocabularies — into the description that ships with the query.</p>
      <p><strong>Custom query</strong> — write SPARQL by hand, run it, save it.
      Nothing the builder does is unavailable here: a compiled comparison
      is ordinary text, and "Edit as custom query" drops it into this editor.</p>
      <p><strong>Edit</strong>, on a saved query's panel on Comparisons, opens
      it where it was made: a comparison built from sides in the builder,
      anything else in this editor. Saving replaces it, also when you renamed
      it, and sets what it opens as. Delete is there too.</p>
      <p><strong>Common cubes</strong> — every cube you and a partner both
      publish breaking down by the same single term that no saved comparison
      covers yet, ready to add. Cubes whose values share a value scheme (a
      survey's agreement items) come as one comparison, grouped by term. Once added they are ordinary comparisons: a good
      place to see how one is written.</p>`,
    howItWorks: `<p>Named queries live in your Pod's
      <code class="mono">queries.json</code>; the Analysis screen composes one
      panel per entry, so a saved query appears there with no rebuild. Deleting
      one removes the panel.</p>
      <p><strong>You supply the query, the backend supplies the sources.</strong>
      SPARQL runs through Comunica on the app backend over a source list chosen
      there — the shared containers this institution already reads. That is why
      no query, however written, can be aimed at the private record container.
      Of those, a query reads only the cubes it names (by
      <code class="mono">#cube id</code>, or every one of a cube's breakdowns);
      one that names none reads them all.</p>
      <p><strong>How the common cubes are found.</strong> Both manifests are
      read for cubes that break down by one term (besides the period), with the
      same measure on each side; a crossed cube does not count, since its small
      combinations were left out. The catalog gives each term its label and, for
      a survey item, its wording, its questionnaire section and what it
      measures.</p>
      <p><strong>The result-shape convention.</strong> A result carrying
      <code class="mono">?value</code> and <code class="mono">?count</code> renders
      as distribution blocks; add <code class="mono">?period</code> for quarter
      chips, and <code class="mono">?publisher</code> or
      <code class="mono">?series</code> to split it into one block per side.
      Anything else renders as a plain table — a fallback, not an error.</p>
      <p><strong>Why a cube can be offered but unavailable.</strong> A query reads
      the shared containers — what was published outbound, plus what partners
      share inbound. A cube sitting in your private cube container is
      unreachable however well it reconciles, so the picker lists it, disables it,
      and says which of those it is. Cubes holds the full picture of every
      cube; the picker deliberately shows only its name and institution.</p>`,
    reference: `<p>Federated SPARQL lets one query span multiple Pod resources
      without copying or caching data centrally. Comunica authenticates as the
      institutional WebID and honours each container's WAC rules; an unreachable
      source contributes an empty result rather than failing the query.</p>`,
  },

  analysis: {
    title: 'Comparisons',
    whatFor: `<p>Read each institution's shared counts side by side, grouped
      by the dataset they compare. Each panel is one saved comparison held in
      your Pod, with one block per publisher; re-run to pick up a newly
      published period, or add a comparison and it appears here.</p>`,
    howItWorks: `<p>The readout is a background job that loads
      <code class="mono">queries.json</code> from your Pod and runs each entry over
      both Pods' shared cube <code class="mono">.ttl</code> files, authenticated as
      the institutional WebID. Nothing here is hardcoded — the panels are composed
      from whatever the Pod holds. The headline gender panel
      <code class="mono">UNION</code>-bridges two vocabularies with no shared
      schema.</p>
      <p><strong>Blocks, not a shared row axis.</strong> Each publisher keeps its
      own vocabulary, its own denominator and its own percentages, so nothing here
      depends on deciding that <code class="mono">Female</code> and
      <code class="mono">Femenino</code> are the same value. The columns are the
      comparison; aligning the labels is a separate question this screen
      deliberately does not answer.</p>
      <p><strong>An empty block is a statement.</strong> A publisher that shares no
      cube answering a query still gets a labelled block, because "publishes
      nothing on this axis" and "is not part of this comparison" are different
      facts. Publishers you cannot read at all get no block — you know nothing
      about what they publish.</p>
      <p><strong>Terms as groups.</strong> A comparison whose
      <code class="mono">?group</code> is a catalog term shows each group with the
      catalog's wording and what it measures, in questionnaire order under
      its section's heading.</p>`,
  },
};
